<template>
  <!-- Download + view live in one actions cell, both always visible with a tooltip. Download is
       still gated behind the DIAGNOSTICS_RETRIEVAL feature + permission; view is passed in as an
       action by the (product-specific) diagnostics tab. Clicking the row repeats its primary action. -->
  <InfuseTable
    :columns="columns"
    :actions="mergedActions"
    :rows="rows"
    :has-more-items="hasMoreItems"
    :loading="loading"
    :refreshable="true"
    :row-clickable="isRowClickable"
    @action="onAction"
    @click-row="onRowClick"
    @load="emit('load')"
    @refresh="emit('refresh')"
  >
    <template #type="{ data }">
      {{ t(`logType.${data}`) }}
    </template>

    <template #createdAt="{ data }">
      {{ formatDate(data as Date) }}
    </template>

    <template #responseStatus="{ data, row }">
      <div class="flex gap-3">
        <InfuseTooltip
          class="flex-initial"
          v-for="status in data"
          :key="status"
          :content="getResponseStatusTooltip(status, row as LogRequest)"
        >
          <InfuseTag :variant="getResponseStatusVariant(status)">
            {{ t(`responseStatus.${status}`) }}
          </InfuseTag>
        </InfuseTooltip>
      </div>
    </template>

    <template #uploadStatus="{ data, row }">
      <InfuseTooltip :content="getUploadStatusTooltip(row as LogRequest)">
        <InfuseTag :variant="getUploadStatusVariant(data as LogUploadStatus)">
          {{ t(`uploadStatus.${data}`) }}
        </InfuseTag>
      </InfuseTooltip>
    </template>

    <template #storageLocation="{ data }">
      <InfuseTag>
        {{ t(`storageLocation.${data}`) }}
      </InfuseTag>
    </template>
  </InfuseTable>
</template>

<script lang="ts" setup>
import { LogRequest, LogResponseStatus, LogStorageLocation, LogUploadStatus } from '@infuse/cpms/charging-station/store';

import { InfuseTable, InfuseTag, InfuseTooltip } from '@infuse/ui/components';
import type { UiRowAction, UiTableColumn, UiTagVariant } from '@infuse/ui/domain';
import { formatDate, hasRequiredPermissions } from '@infuse/ui/utils';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { chargingStationLogRequestsPermissions } from '@infuse/api/cpms/operator';
import { Feature, useFeatureStore } from '@infuse/cpms/shared/features/store';

interface Emits {
  (e: 'load'): void;

  (e: 'refresh'): void;

  (e: 'download', row: LogRequest): void;

  (e: 'action', event: string, row: LogRequest): void;
}

const emit = defineEmits<Emits>();

interface Props {
  rows: LogRequest[];
  loading: boolean;
  page: number;
  hasMoreItems: boolean;
  actions?: UiRowAction[];
}

const props = withDefaults(defineProps<Props>(), {
  rows: () => [],
  loading: false,
  hasMoreItems: true,
  actions: () => []
});

const { t } = useI18n();
const featureStore = useFeatureStore();

// A file only exists to view/download once the station has uploaded it and it is stored internally.
function fileAvailable(row: LogRequest): boolean {
  return row.uploadStatus === LogUploadStatus.UPLOADED && row.storageLocation === LogStorageLocation.INTERNAL;
}

const canDownload = computed(
  () =>
    featureStore.hasFeature(Feature.DIAGNOSTICS_RETRIEVAL) &&
    hasRequiredPermissions([chargingStationLogRequestsPermissions.DOWNLOAD], 'any', false)
);

const downloadAction: UiRowAction = {
  event: 'download',
  icon: ['fal', 'download'],
  label: () => t('action.download'),
  enabled: (row) => fileAvailable(row as LogRequest),
  onlyShowOnHover: false,
  class: 'cursor-pointer px-2 text-neutral-400 hover:text-primary-500'
};

// Download left, then view (passed in by the diagnostics tab) — both in the same cell, always shown.
const mergedActions = computed<UiRowAction[]>(() => [...(canDownload.value ? [downloadAction] : []), ...props.actions]);

function onAction(event: string, row: unknown): void {
  if (event === 'download') {
    emit('download', row as LogRequest);
  } else {
    emit('action', event, row as LogRequest);
  }
}

function primaryActionFor(row: unknown): UiRowAction | undefined {
  return mergedActions.value.find((action) => action.primary && action.enabled(row as LogRequest));
}

function isRowClickable(row: unknown): boolean {
  return primaryActionFor(row) !== undefined;
}

function onRowClick(row: unknown): void {
  const action = primaryActionFor(row);
  if (action) onAction(action.event, row);
}

const columns: UiTableColumn[] = [
  {
    name: 'type',
    label: () => t('table.header.type'),
    field: 'type',
    bold: true
  },
  {
    name: 'createdAt',
    label: () => t('table.header.createdAt'),
    field: 'createdAt'
  },
  {
    name: 'responseStatus',
    label: () => t('table.header.responseStatus'),
    field: 'responseStatus'
  },
  {
    name: 'uploadStatus',
    label: () => t('table.header.uploadStatus'),
    field: 'uploadStatus'
  },
  {
    name: 'storageLocation',
    label: () => t('table.header.storageLocation'),
    field: 'storageLocation'
  },
  {
    name: 'fileName',
    label: () => t('table.header.fileName'),
    field: 'fileName'
  }
];

function getResponseStatusVariant(status: LogResponseStatus): UiTagVariant {
  if (status === LogResponseStatus.CANCELED_PREVIOUS) {
    return 'warning';
  } else if (status === LogResponseStatus.REJECTED) {
    return 'error';
  } else {
    return 'neutral';
  }
}

function getResponseStatusTooltip(logRequestStatus: LogResponseStatus, logRequest: LogRequest): string {
  if (!logRequest.responseStatus || logRequestStatus === LogResponseStatus.NONE) {
    return t('tooltip.responseStatus.unknown');
  } else if (logRequestStatus === LogResponseStatus.CANCELED_PREVIOUS) {
    return t('tooltip.responseStatus.canceledPrevious');
  }

  if (logRequest.errorCode) {
    return t('tooltip.responseStatus.receivedWithErrorCode', {
      date: formatDate(logRequest.responseReceivedAt),
      errorCode: logRequest.errorCode
    });
  } else {
    return t('tooltip.responseStatus.received', {
      date: formatDate(logRequest.responseReceivedAt)
    });
  }
}

function getUploadStatusVariant(status: LogUploadStatus): UiTagVariant {
  if (status === LogUploadStatus.UPLOADING) {
    return 'success';
  } else if (status === LogUploadStatus.ACCEPTED_CANCELED) {
    return 'warning';
  } else if (
    status === LogUploadStatus.UPLOAD_FAILURE ||
    status === LogUploadStatus.BAD_MESSAGE ||
    status === LogUploadStatus.NOT_SUPPORTED_OPERATION ||
    status === LogUploadStatus.PERMISSION_DENIED
  ) {
    return 'error';
  } else {
    return 'neutral';
  }
}

function getUploadStatusTooltip(logRequest: LogRequest): string {
  if (logRequest.uploadStatus && logRequest.uploadStatus !== LogUploadStatus.NONE) {
    return t('tooltip.uploadStatus.received', {
      date: formatDate(logRequest.uploadStatusModifiedAt)
    });
  } else {
    return t('tooltip.uploadStatus.unknown');
  }
}
</script>

<i18n>
{
  "en": {
    "action": {
      "refresh": "Refresh",
      "download": "Download"
    },
    "table.header.type": "Log type",
    "table.header.fileName": "File name",
    "table.header.createdAt": "Start date",
    "table.header.responseStatus": "Response status",
    "table.header.uploadStatus": "Upload status",
    "table.header.storageLocation": "Location",
    "logType": {
      "DATA_COLLECTOR_LOG": "Data collector",
      "DIAGNOSTICS_LOG": "Diagnostics",
      "SECURITY_LOG": "Security"
    },
    "responseStatus": {
      "ACCEPTED": "Accepted",
      "CANCELED_PREVIOUS": "Canceled Ongoing Upload",
      "REJECTED": "Rejected",
      "NONE": "None"
    },
    "uploadStatus": {
      "UPLOADED": "Uploaded",
      "UPLOADING": "Uploading",
      "UPLOAD_FAILURE": "Upload failure",
      "ACCEPTED_CANCELED": "Canceled",
      "BAD_MESSAGE": "Bad message",
      "NOT_SUPPORTED_OPERATION": "Unsupported operation",
      "PERMISSION_DENIED": "Permission denied",
      "IDLE": "Idle",
      "NONE": "None"
    },
    "storageLocation": {
      "EXTERNAL": "External",
      "INTERNAL": "Internal"
    },
    "tooltip": {
      "responseStatus": {
        "received": "Response received at {date}",
        "receivedWithErrorCode": "Response received at {date} with error code: \"{errorCode}\"",
        "canceledPrevious": "This request has canceled an ongoing log file upload",
        "unknown": "No response received (yet)"
      },
      "uploadStatus": {
        "received": "Current status as of {date}",
        "unknown": "No status updates received"
      }
    }
  },
  "nl": {
    "action": {
      "refresh": "Verversen",
      "download": "Downloaden"
    },
    "table.header.type": "Log type",
    "table.header.fileName": "Bestandsnaam",
    "table.header.createdAt": "Startdatum",
    "table.header.responseStatus": "Reactiestatus",
    "table.header.uploadStatus": "Uploadstatus",
    "logType": {
      "DATA_COLLECTOR_LOG": "Data collector",
      "DIAGNOSTICS_LOG": "Diagnostics",
      "SECURITY_LOG": "Security"
    },
    "responseStatus": {
      "ACCEPTED": "Geaccepteerd",
      "CANCELED_PREVIOUS": "Lopende Upload Geannuleerd",
      "REJECTED": "Geweigerd",
      "NONE": "Geen"
    },
    "uploadStatus": {
      "UPLOADED": "Geüpload",
      "UPLOADING": "Aan het uploaden",
      "UPLOAD_FAILURE": "Upload mislukt",
      "ACCEPTED_CANCELED": "Geannuleerd",
      "BAD_MESSAGE": "Fout bericht",
      "NOT_SUPPORTED_OPERATION": "Niet-ondersteunde operatie",
      "PERMISSION_DENIED": "Toestemming geweigerd",
      "IDLE": "Inactief",
      "NONE": "Geen"
    },
    "tooltip": {
      "responseStatus": {
        "received": "Antwoord ontvangen op {date}",
        "receivedWithErrorCode": "Antwoord ontvangen op {date} met error-code: \"{errorCode}\"",
        "canceledPrevious": "Deze aanvraag heeft een lopende upload geannuleerd",
        "unknown": "Nog geen reactie ontvangen"
      },
      "uploadStatus": {
        "received": "Huidige status sinds {date}",
        "unknown": "Nog geen statusupdates ontvangen"
      }
    }
  }
}
</i18n>
